import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  brainLegAvailabilitySummary,
  brainPoolCandidates,
  isRetiredFromCatalog,
  brainLegEntries,
  derivePiEligibleModels,
  isBrainPoolEntry,
  isPiReasoningEffort,
  modelRoleAvailability,
  rememberReviewedModels,
  resolvePiWireRow,
  resolveDefaultBrain,
  parseModelChain,
  resolveBrainGovernanceAgainstCatalog,
  type BrainGovernance,
  type BrainGovernanceChange,
  type BrainPriorityList,
  type ModelCatalogEntry,
  type ModelTierTable,
  type PlatformBrainCuration,
  type PlatformModelDefaultParams,
  type PlatformPiCuration,
  type PlatformPiModelParams,
  type TierTargetFallback,
} from "@dosia/shared";
import { apiCall } from "../lib/api-client.js";
import { authAtom } from "../lib/auth-store.js";
import { useApiQuery } from "../lib/useApiQuery.js";
import { usePlatformModelCatalog } from "../lib/usePlatformModelCatalog.js";
import {
  platformBrainCurationPath,
  platformBrainPriorityPath,
  platformPiCurationPath,
  platformTierTablePath,
  platformUpstreamAvailabilityTestPath,
} from "../lib/api-paths.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { moveIndex } from "../lib/platform-tool-curation-view.js";
import { PriorityListEditor } from "../components/PriorityListEditor.js";
import { PrimaryButton, Select } from "../components/FormField.js";
import {
  anthropicLegBadge,
  priorityBadgeLabel,
  priorityOptionBadge,
  type PriorityBadge,
} from "../lib/priority-badges.js";
import { PlatformToolCuration } from "./PlatformToolCuration.js";
import { PlatformModelDiagnosis } from "./PlatformModelDiagnosis.js";

const baseId = (modelId: string): string => modelId.replace(/\[[^\]]*\]/g, "");
const TIER_ROLES = ["opus", "sonnet", "haiku"] as const;
type TierRole = (typeof TIER_ROLES)[number];
const TIER_LABEL: Record<TierRole, string> = {
  opus: "大档 (opus)",
  sonnet: "中档 (sonnet)",
  haiku: "小档 (haiku)",
};

type Row = { base: string; entry: ModelCatalogEntry; entries: ModelCatalogEntry[] };
type PriorityOption = {
  modelId: string;
  base: string;
  chain: "claude" | "pi";
  entry?: ModelCatalogEntry;
  entries: ModelCatalogEntry[];
};

function priorityRowsFor(
  entries: readonly ModelCatalogEntry[],
  base: string,
  chain: "claude" | "pi",
): ModelCatalogEntry[] {
  return brainLegEntries(entries.filter((entry) => baseId(entry.modelId) === base), chain);
}

/** Claude 主脑列的候选 = 该模型有网关标为上游原生的 anthropic 行（isBrainPoolEntry，与服务端 N-75
 *  收敛 brainPoolCandidates 同一判据；2026-10-08 起按原生协议判，不再按家族名单）。 */
const hasBrainEntry = (entries: readonly ModelCatalogEntry[]): boolean => entries.some(isBrainPoolEntry);

/** 协议 id → 人话短标签。「模型清单」hub 页（PlatformModelCatalogHub.tsx）复用本函数——
 *  单一真相源，禁止在别处重复维护协议命名映射（红线 #9）。 */
export function protocolLabel(protocol: string): string {
  if (protocol === "openai_chat_completions") return "OpenAI Chat Completions";
  if (protocol === "openai_responses") return "OpenAI Responses";
  if (protocol === "anthropic_messages") return "Anthropic Messages";
  if (protocol === "gemini") return "Gemini";
  return protocol || "未知协议";
}

/**
 * 平台级模型治理：网关全量目录是事实源，三份既有策展契约仍分别保存。
 * 旧页面只保留给代码/书签使用，所有超管配置在这里完成。
 */
export function PlatformModelGovernance(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeSection = tabParam === "tools" || tabParam === "diagnose" ? tabParam : "models";
  const token = useAtomValue(authAtom)?.token;
  const { entries, source, loading: catalogLoading } = usePlatformModelCatalog();
  const brainQ = useApiQuery<{ curation: PlatformBrainCuration | null }>(platformBrainCurationPath(), []);
  const piQ = useApiQuery<{ curation: PlatformPiCuration | null }>(platformPiCurationPath(), []);
  const tierQ = useApiQuery<{ tierTable: ModelTierTable | null }>(platformTierTablePath(), []);
  const priorityQ = useApiQuery<{ brainPriority: BrainPriorityList | null }>(platformBrainPriorityPath(), []);
  const [brain, setBrain] = useState<Set<string>>(new Set());
  const [brainParams, setBrainParams] = useState<Record<string, PlatformModelDefaultParams>>({}); // Claude 主脑默认参数（P3）
  const [pi, setPi] = useState<Set<string>>(new Set());
  const [piParams, setPiParams] = useState<Record<string, PlatformPiModelParams>>({});
  const [tiers, setTiers] = useState<ModelTierTable["tiers"]>({});
  const [priority, setPriority] = useState<BrainPriorityList>({ entries: [], editedAt: "", editedBy: "" });
  const [prioritySaving, setPrioritySaving] = useState(false);
  const [prioritySavedAt, setPrioritySavedAt] = useState<string | null>(null);
  const [prioritySaveError, setPrioritySaveError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "brain" | "pi" | "tool">("all");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // 档位表 PUT 回报的「被回落为自动」的映射（B-54）：服务端对目标没勾进主脑池的那几档不落库，
  // 逐条列在这里给超管看，而不是像以前那样整次 400。
  const [tierFallbacks, setTierFallbacks] = useState<TierTargetFallback[]>([]);
  const [testingAvailability, setTestingAvailability] = useState(false);
  const [availabilityTestResult, setAvailabilityTestResult] = useState<{
    available: number;
    unavailable: number;
    testedAt: string;
  } | null>(null);
  const [availabilityTestError, setAvailabilityTestError] = useState<string | null>(null);
  const { t } = useTranslation();

  // 与下发同一判据（N-75，resolveBrainGovernanceAgainstCatalog）：页面上看到的池 = 员工拿到的池——
  // 网关下线的剔除、网关新上的候选并入。**只在 live 清单下收敛**：拿随包快照算会误剔在用模型、
  // 误并快照里有而线上已下线的模型。
  const catalogLive = source === "live" && !catalogLoading;
  const storedGovernance = useMemo<BrainGovernance>(() => ({
    brainCuration: brainQ.data?.curation ?? undefined,
    piCuration: piQ.data?.curation ?? undefined,
    tierTable: tierQ.data?.tierTable ?? undefined,
    brainPriority: priorityQ.data?.brainPriority ?? undefined,
  }), [brainQ.data, piQ.data, tierQ.data, priorityQ.data]);
  // 四份配置**逐份**收敛：每份只随自己的存量与清单重算。合成一次算会让任何一份 refetch（例如保存
  // 优先级后重读）换掉其余几份的收敛结果引用，进而触发下面的 useEffect 把超管尚未保存的勾选冲掉。
  const brainResolution = useMemo(
    () => (catalogLive ? resolveBrainGovernanceAgainstCatalog({ brainCuration: storedGovernance.brainCuration }, entries) : undefined),
    [catalogLive, storedGovernance.brainCuration, entries],
  );
  const piResolution = useMemo(
    () => (catalogLive ? resolveBrainGovernanceAgainstCatalog({ piCuration: storedGovernance.piCuration }, entries) : undefined),
    [catalogLive, storedGovernance.piCuration, entries],
  );
  const tierResolution = useMemo(
    () => (catalogLive ? resolveBrainGovernanceAgainstCatalog({ tierTable: storedGovernance.tierTable }, entries) : undefined),
    [catalogLive, storedGovernance.tierTable, entries],
  );
  const priorityResolution = useMemo(
    () => (catalogLive ? resolveBrainGovernanceAgainstCatalog({ brainPriority: storedGovernance.brainPriority }, entries) : undefined),
    [catalogLive, storedGovernance.brainPriority, entries],
  );
  const governanceResolution = useMemo(() => {
    const parts = [brainResolution, piResolution, tierResolution, priorityResolution];
    if (parts.every((part) => part === undefined)) return undefined;
    return {
      removed: parts.flatMap((part) => part?.removed ?? []),
      retained: parts.flatMap((part) => part?.retained ?? []),
      added: parts.flatMap((part) => part?.added ?? []),
    };
  }, [brainResolution, piResolution, tierResolution, priorityResolution]);
  const effectiveBrainCuration = brainResolution?.governance.brainCuration ?? storedGovernance.brainCuration;
  const effectivePiCuration = piResolution?.governance.piCuration ?? storedGovernance.piCuration;
  const effectiveTierTable = tierResolution?.governance.tierTable ?? storedGovernance.tierTable;
  const candidates = useMemo(() => brainPoolCandidates(entries), [entries]);
  const autoAdded = useMemo(() => {
    const added = governanceResolution?.added ?? [];
    return {
      claude: new Set(added.filter((c) => c.section === "claude-brain").map((c) => c.modelId)),
      pi: new Set(added.filter((c) => c.section === "pi-brain").map((c) => c.modelId)),
    };
  }, [governanceResolution]);
  const retiredPriority = useMemo(() => {
    const map = new Map<string, "removed" | "retained">();
    for (const c of governanceResolution?.removed ?? []) if (c.section === "brain-priority") map.set(c.modelId, "removed");
    for (const c of governanceResolution?.retained ?? []) if (c.section === "brain-priority") map.set(c.modelId, "retained");
    return map;
  }, [governanceResolution]);

  useEffect(() => {
    if (!brainQ.data) return;
    setBrain(new Set((effectiveBrainCuration?.allowedBrainModels ?? []).map(baseId)));
    setBrainParams(effectiveBrainCuration?.modelParams ?? {});
  }, [brainQ.data, effectiveBrainCuration]);
  useEffect(() => {
    if (!piQ.data) return;
    setPi(new Set((effectivePiCuration?.models ?? []).map(baseId)));
    setPiParams(effectivePiCuration?.modelParams ?? {});
  }, [piQ.data, effectivePiCuration]);
  useEffect(() => {
    if (tierQ.data) setTiers(effectiveTierTable?.tiers ?? {});
  }, [tierQ.data, effectiveTierTable]);
  useEffect(() => {
    if (priorityQ.data) {
      setPriority(priorityQ.data.brainPriority ?? { entries: [], editedAt: "", editedBy: "" });
    }
  }, [priorityQ.data]);

  const eligible = useMemo(() => new Set([...derivePiEligibleModels(entries)].map(baseId)), [entries]);
  const rows = useMemo<Row[]>(() => {
    const grouped = new Map<string, ModelCatalogEntry[]>();
    for (const entry of entries) {
      const key = baseId(entry.modelId);
      const list = grouped.get(key) ?? [];
      list.push(entry);
      grouped.set(key, list);
    }
    return [...grouped.entries()]
      .map(([base, entriesForModel]) => ({ base, entries: entriesForModel, entry: entriesForModel[0]! }))
      .sort((a, b) => a.base.localeCompare(b.base));
  }, [entries]);
  const diagnosisModelIds = useMemo(() => rows.map((row) => row.base), [rows]);

  const priorityOptions = useMemo<PriorityOption[]>(() => {
    const bases = [...new Set([...brain, ...pi])].sort((a, b) => a.localeCompare(b));
    const options: PriorityOption[] = [];
    for (const base of bases) {
      if (brain.has(base)) {
        const modelEntries = priorityRowsFor(entries, base, "claude");
        // 默认主脑优先级的 Claude 腿只收 Claude 主脑池成员（有上游原生 anthropic 行），与写入闸同一判据。
        if (hasBrainEntry(modelEntries)) options.push({ modelId: base, base, chain: "claude", entry: modelEntries[0], entries: modelEntries });
      }
      if (pi.has(base)) {
        const modelEntries = priorityRowsFor(entries, base, "pi");
        if (modelEntries.length > 0) options.push({ modelId: base + "[pi]", base, chain: "pi", entry: modelEntries[0], entries: modelEntries });
      }
    }
    return options;
  }, [brain, pi, entries]);

  const priorityItems = useMemo(() => priority.entries.map((item, index) => {
    const { chain } = parseModelChain(item.modelId);
    const base = baseId(item.modelId);
    const matching = priorityOptions.find((option) => option.modelId === item.modelId);
    const modelEntries = matching?.entries ?? priorityRowsFor(entries, base, chain);
    const allModelEntries = entries.filter((catalogEntry) => baseId(catalogEntry.modelId) === base);
    const noBrainLeg = chain === "claude" && allModelEntries.length > 0 && modelEntries.length === 0;
    // 没有 Claude 腿的条目再分两种（N-121 批次 D 审查 MINOR-4）：anthropic 行网关漏发 translated = 原生性未知
    // （「协议待核」+ 网关版本过旧提示，排查方向是网关）；明确是翻译行 / 没有 anthropic 行 = 已供给 · 无主脑腿。
    const unknownNativity = noBrainLeg
      && anthropicLegBadge(allModelEntries.find((entry) => entry.protocol === "anthropic_messages")) === "unknown";
    const retired = retiredPriority.get(item.modelId);
    const badge: PriorityBadge = unknownNativity
      ? "unknown"
      : noBrainLeg
      ? "no_brain_leg"
      : chain === "pi"
        ? "pi"
        : anthropicLegBadge(matching?.entry ?? modelEntries[0]) ?? "unknown";
    return {
      ...item,
      index,
      chain,
      base,
      entry: matching?.entry ?? modelEntries[0],
      entries: modelEntries,
      availability: retired === "removed"
        ? t("admin.modelGovernance.priority.retired")
        : retired === "retained"
          ? t("admin.modelGovernance.priority.retiredKept")
          : modelEntries.length === 0
            ? "不在公开清单"
            : brainLegAvailabilitySummary(modelEntries, chain).label ?? "目录未声明",
      badge,
    };
  }), [priority.entries, priorityOptions, entries, retiredPriority, t]);

  const describeChange = (change: BrainGovernanceChange): string =>
    `${t(`admin.modelGovernance.section.${change.section}`)} · ${change.modelId}`;
  const retiredChanges = useMemo(() => {
    const dedupe = (changes: readonly BrainGovernanceChange[]): BrainGovernanceChange[] => {
      const seen = new Set<string>();
      return changes.filter((c) => {
        const key = `${c.section}|${c.modelId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };
    return { removed: dedupe(governanceResolution?.removed ?? []), retained: dedupe(governanceResolution?.retained ?? []) };
  }, [governanceResolution]);
  // 存量从未记录过「见过的候选集」（N-75 之前保存的配置）：判不出谁是新的，不自动并入，
  // 列出池外候选请超管保存一次建立基线。Claude 池为空 = 全放行，本来就都能用，不列。
  const unreviewedCandidates = useMemo<BrainGovernanceChange[]>(() => {
    if (!catalogLive) return [];
    const out: BrainGovernanceChange[] = [];
    const brainCuration = storedGovernance.brainCuration;
    if (brainCuration?.allowedBrainModels?.length && !brainCuration.reviewedModels) {
      for (const id of candidates.claude) if (!brain.has(id)) out.push({ section: "claude-brain", modelId: id });
    }
    if (!storedGovernance.piCuration?.reviewedModels) {
      for (const id of candidates.pi) if (!pi.has(id)) out.push({ section: "pi-brain", modelId: id });
    }
    return out;
  }, [catalogLive, storedGovernance, candidates, brain, pi]);

  const priorityHasUnknownAnthropicLeg = priorityItems.some((item) => item.chain === "claude" && item.badge === "unknown");

  const priorityPreview = useMemo(() => resolveDefaultBrain({
    priority: priority.entries.length > 0 ? priority : undefined,
    catalog: entries,
    piSelection: [...pi],
  }), [priority, entries, pi]);

  const visibleRows = rows.filter(({ base, entries: modelEntries }) => {
    if (filter === "brain") return hasBrainEntry(modelEntries);
    if (filter === "pi") return eligible.has(base);
    if (filter === "tool") return !hasBrainEntry(modelEntries) && !eligible.has(base);
    return true;
  });

  // 档位目标候选 = 该组的主脑池条目 ∩ **本页已勾选的主脑清单**。
  //
  // 为什么要 ∩ 已勾选（2026-09-10，REQ-293 之后维护者拍板「源头规避」）：档位表原来能选到
  // 任何主脑池模型，包括**没被勾进主脑清单**的。选中之后那个模型的主脑腿会在下发链上被剪掉，
  // 而档位配置还指着它 —— 员工端就拿到一个「档位指着它、模型已经不在清单里」的配置，
  // 登录后第一条消息直接被拒发，弹「企业模型配置正在同步，请稍候重试。」且永不恢复。
  // 这里不让它出现在下拉里，就从源头断掉这条路。
  //
  // `brain` 为空 = 未策展 = 全放行（与本页保存语义、消费端 applyBrainCuration 的 footgun
  // 防护同一口径，别在这里发明「空清单 = 全部禁止」）。
  const groupCandidates = useMemo(() => {
    const byGroup = new Map<string, ModelCatalogEntry[]>();
    for (const entry of entries) {
      if (!isBrainPoolEntry(entry)) continue;
      if (brain.size > 0 && !brain.has(baseId(entry.modelId))) continue;
      const group = entry.chosenGroup?.name;
      if (!group) continue;
      const list = byGroup.get(group) ?? [];
      if (!list.some((candidate) => baseId(candidate.modelId) === baseId(entry.modelId))) list.push(entry);
      byGroup.set(group, list);
    }
    return byGroup;
  }, [entries, brain]);

  const setTier = (main: string, role: TierRole, value: string): void => {
    setTiers((current) => {
      const nextRow = { ...(current[main] ?? {}) };
      if (value) nextRow[role] = value;
      else delete nextRow[role];
      const next = { ...current };
      if (nextRow.opus || nextRow.sonnet || nextRow.haiku) next[main] = nextRow;
      else delete next[main];
      return next;
    });
  };

  const setPiParam = (model: string, patch: Partial<PlatformPiModelParams>): void => {
    setPiParams((current) => {
      const next = { ...current, [model]: { ...(current[model] ?? {}), ...patch } };
      const row = next[model];
      if (!row.reasoningEffort && row.maxCompletionTokens == null) delete next[model];
      return next;
    });
  };

  const setBrainParam = (model: string, patch: Partial<PlatformModelDefaultParams>): void => {
    setBrainParams((current) => {
      const next = { ...current, [model]: { ...(current[model] ?? {}), ...patch } };
      const row = next[model];
      // reasoningEffort 单独可落库：只设 effort 时不得整行删除。
      if (!row.thinking && row.maxTokens == null && !row.reasoningEffort) delete next[model];
      return next;
    });
  };

  // 剔空保护留下的已下线 id：超管加了新模型之后这次保存就该去掉它们（剔后仍非空才去；
  // 剔空会把「只允许这几个」翻成「全部放开 / 不开放」，那种情况原样保留）。
  const withoutRetired = (ids: string[]): string[] => {
    if (!catalogLive) return ids;
    const alive = ids.filter((id) => !isRetiredFromCatalog(id, entries));
    return alive.length > 0 ? alive : ids;
  };

  // 界面上不可编辑的档位行不提交（B-54）：目录里有、但没有主脑腿的模型，三档列显示「Pi 不使用
  // 三档映射」、没有下拉框，存量里即使残留映射（如 gemini 行的 sonnet=…）超管也看不见改不了，
  // 照旧提交只会让服务端替它回落。只在 live 清单下剔——快照可能缺新模型的主脑腿，会误剔有效行；
  // 目录里一行都没有的主模型交给 N-75 收敛（tierResolution）与服务端回落，这里不越权。
  const editableTiers = (): ModelTierTable["tiers"] => {
    if (!catalogLive) return tiers;
    const nonBrainRows = new Set(rows.filter((row) => !hasBrainEntry(row.entries)).map((row) => row.base));
    return Object.fromEntries(Object.entries(tiers).filter(([main]) => !nonBrainRows.has(baseId(main))));
  };

  // 默认主脑优先级的 PUT：「保存默认主脑优先级」与「保存全部治理配置」共用同一份载荷与回填（B-140）。
  // 网关已下线的条目保存时移除（N-75）；剔空保护保留的条目照常提交。
  const putPriority = async (): Promise<void> => {
    const result = await apiCall<{ brainPriority: BrainPriorityList | null }>("PUT", platformBrainPriorityPath(), {
      token,
      body: { entries: priority.entries.filter((entry) => retiredPriority.get(entry.modelId) !== "removed").map((entry) => entry.modelId) },
    });
    setPriority(result.brainPriority ?? { entries: [], editedAt: "", editedBy: "" });
    setPrioritySavedAt(new Date().toLocaleTimeString());
    await priorityQ.refetch();
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    setPrioritySaveError(null);
    setTierFallbacks([]);
    try {
      // reviewedModels（N-75）：只在 live 清单下提交「这次见过的候选」，之后网关新上的才会被自动并入；
      // 清单不可达时省略，服务端保留存量记录。N-115：候选只含此刻能勾的（上游不提供的不算见过——
      // 恢复后按新上并入），上次记录里仍在清单的保留（可选时明确没勾的不会因维护期保存被忘掉）。
      await apiCall("PUT", platformBrainCurationPath(), {
        token,
        body: {
          allowedBrainModels: withoutRetired([...brain]),
          modelParams: brainParams,
          ...(catalogLive
            ? { reviewedModels: rememberReviewedModels(storedGovernance.brainCuration?.reviewedModels, candidates.claude, entries) }
            : {}),
        },
      });
      await apiCall("PUT", platformPiCurationPath(), {
        token,
        body: {
          models: withoutRetired([...pi]),
          modelParams: piParams,
          ...(catalogLive
            ? { reviewedModels: rememberReviewedModels(storedGovernance.piCuration?.reviewedModels, candidates.pi, entries) }
            : {}),
        },
      });
      const tierResult = await apiCall<{ tierTable: ModelTierTable | null; fallbacks?: TierTargetFallback[] }>(
        "PUT",
        platformTierTablePath(),
        { token, body: { tiers: editableTiers() } },
      );
      setTierFallbacks(Array.isArray(tierResult?.fallbacks) ? tierResult.fallbacks : []);
      // 「全部」含默认主脑优先级（B-140，issue #3503）。必须排在主脑 / Pi 池之后：优先级写闸按已存的两个池
      // 判新增条目（not_in_brain_curation / not_in_pi_curation），同一次保存里刚勾进池的模型要先落库才过得了闸。
      // 优先级被闸拒绝时前三块已写入，照实说，不翻「已保存」。
      let priorityOk = true;
      try {
        await putPriority();
      } catch (error) {
        priorityOk = false;
        setPrioritySaveError(error instanceof Error ? error.message : String(error));
        setSaveError("主脑、Pi、档位已写入；默认主脑优先级未保存，原因见下方");
      }
      if (priorityOk) setSavedAt(new Date().toLocaleTimeString());
      // 重读存量：下线提示 / 未记录基线提示按新存量重算，不留「已保存却还在提示」。
      await Promise.all([brainQ.refetch(), piQ.refetch(), tierQ.refetch()]);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setSaving(false);
    }
  };

  const movePriority = (index: number, delta: -1 | 1): void => {
    setPriority((current) => ({ ...current, entries: moveIndex(current.entries, index, delta) }));
  };

  const removePriority = (index: number): void => {
    setPriority((current) => ({ ...current, entries: current.entries.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const addPriority = (modelId: string): void => {
    if (!modelId || priority.entries.some((item) => item.modelId === modelId)) return;
    setPriority((current) => ({ ...current, entries: [...current.entries, { modelId }] }));
  };

  const savePriority = async (): Promise<void> => {
    setPrioritySaving(true);
    setPrioritySaveError(null);
    try {
      await putPriority();
    } catch (error) {
      setPrioritySaveError(error instanceof Error ? error.message : String(error));
    } finally {
      setPrioritySaving(false);
    }
  };

  const testUpstreamAvailability = async (): Promise<void> => {
    setTestingAvailability(true);
    setAvailabilityTestError(null);
    try {
      const result = await apiCall<{ available: number; unavailable: number; testedAt: string }>(
        "POST",
        platformUpstreamAvailabilityTestPath(),
        { token, body: { scope: "stale" } },
      );
      setAvailabilityTestResult(result);
    } catch (error) {
      setAvailabilityTestError(error instanceof Error ? error.message : String(error));
    } finally {
      setTestingAvailability(false);
    }
  };

  const toggle = (set: Set<string>, setter: (next: Set<string>) => void, model: string): void => {
    const next = new Set(set);
    if (next.has(model)) next.delete(model);
    else next.add(model);
    setter(next);
  };
  const togglePi = (model: string): void => {
    toggle(pi, setPi, model);
    if (pi.has(model)) setPiParams((current) => { const next = { ...current }; delete next[model]; return next; });
  };
  const toggleBrain = (model: string): void => {
    toggle(brain, setBrain, model);
    if (brain.has(model)) setBrainParams((current) => { const next = { ...current }; delete next[model]; return next; });
  };

  return (
    <div className="max-w-6xl space-y-4">
      <header>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">平台模型治理</h1>
            <p className="mt-1 text-sm text-dosia-text-secondary">
              网关全量模型、分组与协议事实统一展示；主脑池、Pi 池和 Claude 三档映射在此配置，全部平台生效。
            </p>
          </div>
          <span className="text-xs text-dosia-text-secondary">
            {source === "live" ? "网关实时目录" : ""}{catalogLoading ? " · 加载中…" : ""}
          </span>
        </div>
        {source !== "live" && !catalogLoading && (
          // 网关不可达：候选如实留空（不回落随包快照，N-95），hook 每 30s 后台重拉，恢复后自动出现。
          <div className="mt-2 rounded border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-xs font-medium text-amber-400">
            {t("admin.modelCatalog.unavailable")}
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-3">
          <PrimaryButton onClick={testUpstreamAvailability} disabled={testingAvailability}>
            {testingAvailability ? "测试中…" : "⟳ 一键测试上游可用性"}
          </PrimaryButton>
          {availabilityTestResult && (
            <span className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-400">
              已更新 · 可用 {availabilityTestResult.available} / 不可用 {availabilityTestResult.unavailable}
              {" · "}
              {new Date(availabilityTestResult.testedAt).toLocaleString()}
            </span>
          )}
        </div>
        {availabilityTestError && (
          <ErrorBanner className="mt-2">测试失败：{availabilityTestError}</ErrorBanner>
        )}
      </header>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="平台治理页签">
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === "models"}
          onClick={() => setSearchParams({})}
          className={`rounded px-3 py-1.5 text-sm ${activeSection === "models" ? "bg-dosia-accent/15 text-dosia-text" : "text-dosia-text-secondary hover:bg-dosia-bg-lighter"}`}
        >
          模型治理
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === "tools"}
          onClick={() => setSearchParams({ tab: "tools" })}
          className={`rounded px-3 py-1.5 text-sm ${activeSection === "tools" ? "bg-dosia-accent/15 text-dosia-text" : "text-dosia-text-secondary hover:bg-dosia-bg-lighter"}`}
        >
          内置工具路由
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === "diagnose"}
          onClick={() => setSearchParams({ tab: "diagnose" })}
          className={`rounded px-3 py-1.5 text-sm ${activeSection === "diagnose" ? "bg-dosia-accent/15 text-dosia-text" : "text-dosia-text-secondary hover:bg-dosia-bg-lighter"}`}
        >
          {t("admin.modelGovernance.tab.diagnose")}
        </button>
      </div>

      {activeSection === "tools" ? (
        <PlatformToolCuration />
      ) : activeSection === "diagnose" ? (
        <PlatformModelDiagnosis modelIds={diagnosisModelIds} />
      ) : (
        <div data-testid="model-governance-panel" className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ["模型", rows.length],
          ["分组", new Set(entries.flatMap((entry) => [entry.chosenGroup?.name, ...(entry.availableGroups ?? []).map((group) => group.name)])).size],
          ["Pi 候选", eligible.size],
        ].map(([label, value]) => (
          <div key={label} className="rounded border border-dosia-border bg-dosia-bg-light px-3 py-2">
            <div className="text-xs text-dosia-text-secondary">{label}</div>
            <div className="mt-1 text-xl font-semibold text-dosia-text">{value}</div>
          </div>
        ))}
      </div>

      <div className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-400">
        GPT 经 OpenAI Chat Completions 目录行纳入 Pi 候选；Pi 执行腿按模型族自动走 Responses 协议，与目录行协议解耦。公开目录的 `openai_responses` 行待网关快照补发（不影响勾选与执行）。Kimi K3、DeepSeek、GLM 等 Pi 候选走 OpenAI Chat Completions，Pi 不使用 Claude 三档映射。
        模型名下方的琥珀色小字（如有）来自网关组×模型×协议可用性契约（available / unavailable_reason，可选字段，契约未部署时不显示）。上游暂不可用时禁止新增勾选、也不会被自动加入池，已加入的模型仍可取消；上游恢复后维护期间新上的模型才按「新上线」自动加入。
      </div>

      {(retiredChanges.removed.length > 0 || retiredChanges.retained.length > 0) && (
        <div data-testid="retired-models-banner" className="rounded border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-xs text-amber-400">
          {retiredChanges.removed.length > 0 && (
            <>
              <div className="font-medium">{t("admin.modelGovernance.retiredBanner.title", { count: retiredChanges.removed.length })}</div>
              <div className="mt-1">{retiredChanges.removed.map(describeChange).join("、")}</div>
            </>
          )}
          {retiredChanges.retained.length > 0 && (
            <div className={retiredChanges.removed.length > 0 ? "mt-1" : undefined}>
              {t("admin.modelGovernance.retiredBanner.retained")}
              {retiredChanges.retained.map(describeChange).join("、")}
            </div>
          )}
        </div>
      )}
      {unreviewedCandidates.length > 0 && (
        <div data-testid="unreviewed-candidates-banner" className="rounded border border-dosia-border bg-dosia-bg-light px-3 py-2 text-xs text-dosia-text-secondary">
          <div className="font-medium text-dosia-text">{t("admin.modelGovernance.baselineBanner.title", { count: unreviewedCandidates.length })}</div>
          <div className="mt-1">{unreviewedCandidates.map(describeChange).join("、")}</div>
          <div className="mt-1">{t("admin.modelGovernance.baselineBanner.hint")}</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="模型治理筛选">
        {([
          ["all", "全部模型"], ["brain", "Claude 主脑池"], ["pi", "Pi 主脑池"], ["tool", "工具 / 图片"],
        ] as const).map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={filter === key} onClick={() => setFilter(key)}
            className={`rounded px-3 py-1.5 text-sm ${filter === key ? "bg-dosia-accent/15 text-dosia-text" : "text-dosia-text-secondary hover:bg-dosia-bg-lighter"}`}>
            {label}
          </button>
        ))}
      </div>

      {(brainQ.error || piQ.error || tierQ.error || priorityQ.error) && <ErrorBanner>{brainQ.error?.message ?? piQ.error?.message ?? tierQ.error?.message ?? priorityQ.error?.message}</ErrorBanner>}
      {saveError && <ErrorBanner>保存失败：{saveError}</ErrorBanner>}
      {prioritySaveError && <ErrorBanner>默认主脑优先级保存失败：{prioritySaveError}</ErrorBanner>}

      <div className="overflow-x-auto rounded-lg border border-dosia-border">
        <table className="w-full text-sm">
          <thead className="bg-dosia-bg-light text-dosia-text-secondary">
            <tr>
              <th className="px-3 py-2 text-left font-medium">模型 / 协议</th>
              <th className="px-3 py-2 text-left font-medium">分组</th>
              <th className="px-3 py-2 text-left font-medium">Claude 主脑</th>
              <th className="px-3 py-2 text-left font-medium">Pi 主脑</th>
              <th className="px-3 py-2 text-left font-medium">三档映射</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(({ base, entry, entries: modelEntries }) => {
              const brainRow = hasBrainEntry(modelEntries);
              const brainEntry = modelEntries.find(isBrainPoolEntry) ?? entry;
              const brainThinkingParam = brainEntry.capabilities?.thinkingParam;
              const brainHasThinkingControl = brainThinkingParam?.supported === true;
              const brainOutputTokenParam = brainEntry.capabilities?.outputTokenParam;
              const brainMaxTokens = typeof brainOutputTokenParam?.max === "number" && brainOutputTokenParam.max >= 1
                ? brainOutputTokenParam.max
                : undefined;
              // SDK effort 档位：网关 capabilities.supportedEffortLevels（模型级，如 kimi-k3=[low,high,max]）。
              const brainEffortLevels = (brainEntry.capabilities?.supportedEffortLevels ?? []).filter(
                (level): level is NonNullable<PlatformModelDefaultParams["reasoningEffort"]> =>
                  typeof level === "string" && ["low", "medium", "high", "xhigh", "max"].includes(level),
              );
              const brainHasEffortControl = brainEffortLevels.length > 0;
              // thinkingParam.default 是 thinking 模式默认（如 enabled），不是 effort 档位；
              // 仅当 default 恰好是 supportedEffortLevels 成员时才展示「（档位）」。
              const brainEffortDefault =
                typeof brainThinkingParam?.default === "string" &&
                (brainEffortLevels as string[]).includes(brainThinkingParam.default)
                  ? brainThinkingParam.default
                  : undefined;
              const brainEffortDefaultLabel = brainEffortDefault
                ? `跟随网关默认（${brainEffortDefault}）`
                : "跟随网关默认";
              const hasBrainParamControls = brainHasThinkingControl || brainOutputTokenParam !== undefined || brainHasEffortControl;
              const piCandidate = eligible.has(base);
              // pi 走哪种接口 = 清单为该模型选中的那条上游原生行（shared 唯一规则，N-121 批次 B）；Pi 参数控件读同一行的能力。
              const piWireRow = resolvePiWireRow(modelEntries, base);
              const piEntry = piWireRow ?? entry;
              const piDescriptor = piEntry.capabilities?.thinkingParam;
              const piLevels = piDescriptor?.supported === true
                ? (piDescriptor.levels ?? []).filter(isPiReasoningEffort)
                : [];
              const piDefaultLabel = piDescriptor?.default ? `跟随网关默认（${piDescriptor.default}）` : "跟随网关默认（目录未声明）";
              const outputTokenParam = piEntry.capabilities?.outputTokenParam;
              const piDefaultTokens = typeof outputTokenParam?.default === "number" && outputTokenParam.default >= 1
                ? outputTokenParam.default
                : undefined;
              const piMaxTokens = typeof outputTokenParam?.max === "number" && outputTokenParam.max >= 1
                ? outputTokenParam.max
                : undefined;
              const group = brainEntry.chosenGroup?.name;
              const candidates = group ? groupCandidates.get(group) ?? [] : [];
              const groupNames = [...new Set(modelEntries.flatMap((item) => [item.chosenGroup?.name, ...(item.availableGroups ?? []).map((itemGroup) => itemGroup.name)].filter((name): name is string => Boolean(name))))];
              const hasPiParamControls = piLevels.length > 0 || outputTokenParam !== undefined;
              // 上游可用性单一判定入口（红线 #9）——blocked 只用于阻断新增勾选，已勾选项
              // 保持可切（管理员仍能手动移除，见 disabled 表达式）；note 与 blocked 解耦展示。
              const brainAvail = brainRow ? modelRoleAvailability(modelEntries, "brain") : { blocked: false, note: null };
              const piAvail = piCandidate ? modelRoleAvailability(modelEntries, "pi") : { blocked: false, note: null };
              return (
                <tr key={base} className="border-t border-dosia-border align-top">
                  <td className="px-3 py-3">
                    <div className="font-medium text-dosia-text">{entry.displayName || base}</div>
                    <div className="text-xs text-dosia-text-secondary">{base}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {[...new Set(modelEntries.map((item) => protocolLabel(item.protocol)))].map((label) => <span key={label} className="rounded bg-dosia-bg-lighter px-1.5 py-0.5 text-[10px] text-dosia-text-secondary">{label}</span>)}
                    </div>
                    {base === "kimi-k3" && <div className="mt-1 text-xs text-dosia-accent">Pi 内测 · openai-completions · 当前仅文本输入，图片/视频待接入</div>}
                  </td>
                  <td className="px-3 py-3 text-dosia-text-secondary">
                    <div className="flex flex-wrap gap-1">{(groupNames.length > 0 ? groupNames : ["—"]).map((name) => <span key={name} className="rounded bg-dosia-bg-lighter px-1.5 py-0.5 text-xs">{name}</span>)}</div>
                    <div className="mt-1 text-xs">{modelEntries.length > 1 ? `${modelEntries.length} 个协议入口` : ""}</div>
                  </td>
                  <td className="px-3 py-3">
                    {brainRow ? (
                      <div className="space-y-2">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={brain.has(base)}
                            disabled={brainAvail.blocked && !brain.has(base)}
                            onChange={() => toggleBrain(base)}
                            aria-label={`允许 ${base} 作 Claude 主脑`}
                            aria-describedby={brainAvail.note ? `platform-model-unavailable-brain-${base}` : undefined}
                            title={brainAvail.note ?? undefined}
                          />
                          <span>{brain.has(base) ? "已加入" : "可加入"}</span>
                        </label>
                        {brainAvail.note && <div id={`platform-model-unavailable-brain-${base}`} className="text-[11px] text-amber-400">{brainAvail.note}</div>}
                        {brainAvail.blocked && brain.has(base) && <div data-testid={`brain-selected-unavailable-${base}`} className="text-[11px] text-amber-400">{t("admin.modelGovernance.selectedUnavailable")}</div>}
                        {autoAdded.claude.has(base) && brain.has(base) && <div className="text-[11px] text-dosia-accent">{t("admin.modelGovernance.autoAdded")}</div>}
                        {brain.has(base) && hasBrainParamControls && (
                          <div className="space-y-1 text-xs">
                            {brainHasThinkingControl && (
                              <Select
                                value={brainParams[base]?.thinking ?? ""}
                                onChange={(event) => setBrainParam(base, {
                                  thinking: event.target.value ? event.target.value as PlatformModelDefaultParams["thinking"] : undefined,
                                })}
                                aria-label={`Thinking 模式-${base}`}
                              >
                                <option value="">跟随网关默认</option>
                                <option value="enabled">enabled</option>
                                <option value="disabled">disabled</option>
                              </Select>
                            )}
                            {brainHasEffortControl && (
                              <Select
                                value={brainParams[base]?.reasoningEffort ?? ""}
                                onChange={(event) => setBrainParam(base, {
                                  reasoningEffort: event.target.value
                                    ? event.target.value as PlatformModelDefaultParams["reasoningEffort"]
                                    : undefined,
                                })}
                                aria-label={`SDK 推理档位-${base}`}
                              >
                                <option value="">{brainEffortDefaultLabel}</option>
                                {brainEffortLevels.map((level) => (
                                  <option key={level} value={level}>{level}</option>
                                ))}
                              </Select>
                            )}
                            {brainOutputTokenParam && (
                              <div className="space-y-1">
                                <div className="text-dosia-text-secondary">
                                  默认 {brainOutputTokenParam.default ?? "网关默认"}
                                  {brainMaxTokens !== undefined ? ` / 可配置上限 ${brainMaxTokens}` : ""}
                                </div>
                                {brainMaxTokens !== undefined && (
                                  <input
                                    className="w-36 rounded border border-dosia-border bg-dosia-bg px-2 py-1 text-xs"
                                    type="number"
                                    min={1}
                                    max={brainMaxTokens}
                                    placeholder="不填写则跟随默认"
                                    value={brainParams[base]?.maxTokens ?? ""}
                                    onChange={(event) => setBrainParam(base, {
                                      maxTokens: event.target.value ? Number(event.target.value) : undefined,
                                    })}
                                    aria-label={`最大输出 token-${base}`}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : <span className="text-xs text-dosia-text-secondary">不适用</span>}
                  </td>
                  <td className="px-3 py-3">
                    {piCandidate ? (
                      <div className="space-y-2">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={pi.has(base)}
                            disabled={piAvail.blocked && !pi.has(base)}
                            onChange={() => togglePi(base)}
                            aria-label={`允许 ${base} 作为 Pi 主脑`}
                            aria-describedby={piAvail.note ? `platform-model-unavailable-pi-${base}` : undefined}
                            title={piAvail.note ?? undefined}
                          />
                          <span>{pi.has(base) ? "已加入" : "可加入"}</span>
                        </label>
                        {piWireRow && (
                          <div>
                            <span data-testid={`platform-model-pi-wire-${base}`} className="rounded bg-dosia-bg-lighter px-1.5 py-0.5 text-[10px] text-dosia-text-secondary">{protocolLabel(piWireRow.protocol)}</span>
                          </div>
                        )}
                        {piAvail.note && <div id={`platform-model-unavailable-pi-${base}`} className="text-[11px] text-amber-400">{piAvail.note}</div>}
                        {piAvail.blocked && pi.has(base) && <div data-testid={`pi-selected-unavailable-${base}`} className="text-[11px] text-amber-400">{t("admin.modelGovernance.selectedUnavailable")}</div>}
                        {autoAdded.pi.has(base) && pi.has(base) && <div className="text-[11px] text-dosia-accent">{t("admin.modelGovernance.autoAdded")}</div>}
                        {pi.has(base) && (
                          hasPiParamControls ? (
                            <div className="space-y-1 text-xs">
                              {piLevels.length > 0 && (
                                <Select
                                  value={piParams[base]?.reasoningEffort ?? ""}
                                  onChange={(event) => setPiParam(base, {
                                    reasoningEffort: event.target.value
                                      ? event.target.value as PlatformPiModelParams["reasoningEffort"]
                                      : undefined,
                                  })}
                                  aria-label={`Pi 推理档位-${base}`}
                                >
                                  <option value="">{piDefaultLabel}</option>
                                  {piLevels.map((level) => <option key={level} value={level}>{level}</option>)}
                                </Select>
                              )}
                              {outputTokenParam && (
                                <div className="space-y-1">
                                  <div className="text-dosia-text-secondary">
                                    默认 {piDefaultTokens ?? "网关默认"}
                                    {piMaxTokens !== undefined ? ` / 可配置上限 ${piMaxTokens}` : ""}
                                  </div>
                                  {piMaxTokens !== undefined && (
                                    <input
                                      className="w-36 rounded border border-dosia-border bg-dosia-bg px-2 py-1 text-xs"
                                      type="number"
                                      min={1}
                                      max={piMaxTokens}
                                      placeholder="不填写则跟随默认"
                                      value={piParams[base]?.maxCompletionTokens ?? ""}
                                      onChange={(event) => setPiParam(base, {
                                        maxCompletionTokens: event.target.value ? Number(event.target.value) : undefined,
                                      })}
                                      aria-label={`Pi 最大输出 token-${base}`}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-dosia-text-secondary">按网关能力目录，未提供平台覆盖</span>
                          )
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-dosia-text-secondary">不适用</span>
                    )}
                  </td>
                  <td className="min-w-[420px] px-3 py-3">
                    {brainRow ? <div className="grid grid-cols-3 gap-2">{TIER_ROLES.map((role) => {
                      const current = tiers[base]?.[role] ?? "";
                      // 存量值指向一个**已经不在勾选清单里**的模型时，它不在 candidates 里 ——
                      // 直接渲染会让 Select 显示空白，超管以为「没配」，其实库里还存着那个值，
                      // 下发链上被剪掉后就是员工端那句「企业模型配置正在同步」。把它显式补成一个
                      // 带警告的选项，让它可见、可改（选「自动」即清掉）。
                      const stale = current !== "" && !candidates.some((c) => baseId(c.modelId) === current);
                      return (
                        <Select key={role} value={current} onChange={(event) => setTier(base, role, event.target.value)} aria-label={`${TIER_LABEL[role]}-${base}`}>
                          <option value="">自动</option>
                          {stale && <option value={current}>{`${current}（未勾选，不会生效）`}</option>}
                          {candidates.map((candidate) => <option key={baseId(candidate.modelId)} value={baseId(candidate.modelId)}>{candidate.displayName || baseId(candidate.modelId)}</option>)}
                        </Select>
                      );
                    })}</div> : <span className="text-xs text-dosia-text-secondary">Pi 不使用三档映射</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <PriorityListEditor
        title="默认主脑优先级（全平台）"
        blurb="所有用户默认跟随；组织可在模型圈定里禁用个别模型。可用性按网关运行时清单判定，后台只存顺序。"
        items={priorityItems.map((item) => ({
          modelId: item.modelId,
          name: item.entry?.displayName || item.base,
          subtitle: item.modelId,
          badge: item.badge,
          availability: item.availability,
        }))}
        options={priorityOptions
          .filter((option) => !priority.entries.some((item) => item.modelId === option.modelId))
          .map((option) => ({
            modelId: option.modelId,
            label: (option.entry?.displayName || option.base) + " · " + option.modelId + " · " + priorityBadgeLabel(priorityOptionBadge(option)),
          }))}
        onMove={movePriority}
        onRemove={removePriority}
        onAdd={addPriority}
        onSave={() => { void savePriority(); }}
        saving={prioritySaving}
        savedAt={prioritySavedAt}
        addLabel="添加默认主脑优先级"
        addPlaceholder="添加主脑模型…"
        saveLabel="保存默认主脑优先级"
        ariaPrefix="主脑优先级"
        testId="brain-priority-editor"
      >
        <div data-testid="brain-priority-preview" className="rounded border border-dosia-border bg-dosia-bg px-3 py-2 text-sm">
          <div className="font-medium text-dosia-text">按平台公开清单预览，各组织实际供给可能不同</div>
          <div className="mt-1 text-dosia-text-secondary">
            {priorityPreview.ok
              ? "此刻会解析到 " + priorityPreview.chosen.modelId
              : "此刻暂无可用默认主脑（" + priorityPreview.reason + "）"}
          </div>
          {priorityPreview.skipped.length > 0 && (
            <div className="mt-1 text-xs text-dosia-text-secondary">
              已跳过：{priorityPreview.skipped.map((item) => item.modelId + (item.unavailableReason ? "（" + item.unavailableReason + "）" : "")).join("、")}
            </div>
          )}
          {priorityHasUnknownAnthropicLeg && (
            <div className="mt-1 text-xs text-dosia-text-secondary" role="note">
              这条腿的原生性未知，可能网关版本过旧
            </div>
          )}
        </div>
      </PriorityListEditor>

      <div className="flex items-center gap-3">
        <PrimaryButton onClick={save} disabled={saving}>{saving ? "保存中…" : "保存全部治理配置"}</PrimaryButton>
        <span className="text-xs text-dosia-text-secondary">{savedAt ? `已保存 ${savedAt}` : "保存会分别写入主脑、Pi、档位、默认主脑优先级四个既有接口"}</span>
      </div>
      {tierFallbacks.length > 0 && (
        <div data-testid="tier-fallback-notice" role="status" className="rounded border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-xs text-amber-400">
          {t("admin.modelGovernance.tierFallback.title")}
          {tierFallbacks.map((item) => t("admin.modelGovernance.tierFallback.item", { mainModel: item.mainModel, tier: item.tier, target: item.target })).join("、")}
        </div>
      )}
        </div>
      )}
    </div>
  );
}
